import * as ngCore from '@angular/core'
import * as router from '@attain-apps/fe/web/main/lib/router'

import * as directives from '../../directives'
import * as models from '../../models'

/**
 * The Tabs component is used to display content in a tabbed manner.
 * It allows users to switch between different sections of content by clicking on the corresponding tabs.
 * each tab content needs to be wrapped in a {@link tab.Component|Tab} component.
 *
 * **Inputs**
 * - {@link AtnDsAbstractTabs.disabledSg|disabled}?
 * - {@link AtnDsAbstractTabs.enableQueryParamPersistenceUnderKeySg|enableQueryParamPersistenceUnderKey}?
 *
 * **Outputs**
 * - {@link AtnDsAbstractTabs.selectedTabChange|selectedTabChange}
 *
 */
@ngCore.Directive()
export abstract class AtnDsAbstractTabs implements ngCore.OnDestroy {
  /** @see {@link router.Router} */
  private readonly _router = ngCore.inject(router.Router, { optional: true })
  /** Tracks whether the browser is unloading (refresh/close). */
  private _isUnloading = false
  /** User-selected tab index that takes effect immediately while persistence catches up. */
  private readonly _selectedIndexOverrideSg = ngCore.signal<number | undefined>(
    undefined,
  )
  /** @see {@link directives.tab.Directive} */
  public readonly tabsSg = ngCore.contentChildren(directives.tab.Directive, {
    descendants: true,
  })

  /** Whether all tabs are disabled. */
  public readonly disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })
  /**
   * Enables query-param persistence for tab selection under the given key.
   *
   * @example
   * ```html
   * <atn-ds-floating-horizontal-tabs
   *   enableQueryParamPersistenceUnderKey="dmsEntityTab"
   * >
   * ```
   *
   * URL example after selecting a non-default tab:
   * `/portal/tt/main/dms/entity?dmsEntityTab=data`
   */
  public readonly enableQueryParamPersistenceUnderKeySg = ngCore.input<
    string | undefined
  >(undefined, {
    alias: 'enableQueryParamPersistenceUnderKey',
  })

  /**
   * Signal representing the index of the currently {@link selectedTabSg}. Defaults to 0
   * (first tab).
   */
  public readonly selectedIndexSg = ngCore.computed(
    (
      tabs = this.tabsSg(),
      selectedIndexOverride = this._selectedIndexOverrideSg(),
      persistenceKey = this.enableQueryParamPersistenceUnderKeySg()?.trim() ??
        undefined,
    ) => {
      if (!tabs?.length) {
        return 0
      }

      const fallbackIndex = this._defaultSelectedIndexSg()
      if (!persistenceKey || !this._router) {
        return selectedIndexOverride ?? fallbackIndex
      }

      const queryValue =
        this._router.selectors.queryParams.asSignal()?.[persistenceKey]
      const tokens = tabs.map((tab, index) =>
        models.Tab.stringify({ index, name: tab.nameSg() }),
      )
      const persistedIndex = tokens.findIndex(token => token === queryValue)

      return (
        selectedIndexOverride ??
        (persistedIndex >= 0 ? persistedIndex : fallbackIndex)
      )
    },
  )

  /**
   *  Signal representing the currently selected tab payload, which includes
   * the tab index and name.
   */
  public readonly selectedTabSg = ngCore.computed(
    (tabs = this.tabsSg(), selectedIndex = this.selectedIndexSg()) =>
      tabs?.find((_, i) => i === selectedIndex),
  )
  /** First non-disabled tab that opts into initial selection, if one exists. */
  private readonly _initiallySelectedIndexSg = ngCore.computed(
    (tabs = this.tabsSg()) =>
      tabs?.findIndex(
        tab => !tab.disabledSg() && tab.isInitiallySelectedSg(),
      ) ?? -1,
  )
  /** Default tab index used when persistence has no explicit query-param value. */
  private readonly _defaultSelectedIndexSg = ngCore.computed(() =>
    Math.max(this._initiallySelectedIndexSg(), 0),
  )
  /**
   * Emits when tab selection changes.
   */
  public readonly selectedTabChange = ngCore.output<models.Tab | undefined>()

  constructor() {
    /**
     * Emits the selected tab payload whenever tabs or selected index changes.
     */
    ngCore.effect(() => {
      const tabs = this.tabsSg()
      if (!tabs?.length) {
        return
      }

      const selectedIndex = this.selectedIndexSg()
      const selectedTab = this.selectedTabSg()
      this.selectedTabChange.emit(
        selectedTab
          ? { index: selectedIndex, name: selectedTab.nameSg() }
          : undefined,
      )
    })

    /**
     * Clears the local override after URL persistence catches up so later router
     * changes continue to drive selection.
     */
    ngCore.effect(() => {
      const persistenceKey =
        this.enableQueryParamPersistenceUnderKeySg()?.trim() ?? undefined
      const selectedIndexOverride = this._selectedIndexOverrideSg()
      if (
        selectedIndexOverride === undefined ||
        !persistenceKey ||
        !this._router
      ) {
        return
      }

      const tabs = this.tabsSg()
      if (!tabs?.length) {
        return
      }

      const queryValue =
        this._router.selectors.queryParams.asSignal()?.[persistenceKey]
      const tokens = tabs.map((tab, index) =>
        models.Tab.stringify({ index, name: tab.nameSg() }),
      )
      const fallbackIndex = this._defaultSelectedIndexSg()
      const persistedIndex = tokens.findIndex(token => token === queryValue)
      const desiredIndex = persistedIndex >= 0 ? persistedIndex : fallbackIndex

      if (desiredIndex === selectedIndexOverride) {
        this._selectedIndexOverrideSg.set(undefined)
      }
    })
  }

  /** Handles click events on tabs, updating the selected index and synchronizing*/
  public onTabClickHandler(index: number): void {
    this._selectedIndexOverrideSg.set(index)

    const persistenceKey =
      this.enableQueryParamPersistenceUnderKeySg()?.trim() ?? undefined
    if (!persistenceKey || !this._router) {
      return
    }

    const clickedTab = this.tabsSg()?.[index]
    if (!clickedTab) {
      return
    }

    const queryValue =
      index === this._defaultSelectedIndexSg()
        ? null
        : models.Tab.stringify({ index, name: clickedTab.nameSg() })
    this._router.actions.navigate
      .from('AtnDsAbstractTabs.onTabClickHandler')
      .dispatch({
        queryParams: {
          plain: {
            [persistenceKey]: queryValue,
          },
        },
      })
  }

  /**
   * Marks this instance as unloading so destroy cleanup does not run on refresh.
   */
  @ngCore.HostListener('window:beforeunload')
  protected beforeUnloadHandler(): void {
    this._isUnloading = true
  }

  /**
   * Clears persisted tab query param when this tabset is destroyed during
   * in-app navigation so tab state does not leak to later visits.
   *
   * Skipped when an Angular navigation is already in progress because Angular
   * uses deferred URL updates — `window.location` still reflects the **old**
   * URL at this point.  Dispatching a navigate action here would read that
   * stale location and inadvertently cancel the in-flight navigation, sending
   * the user back to the previous page.
   */
  ngOnDestroy(): void {
    if (this._isUnloading) {
      return
    }

    // When Angular Router is mid-navigation (e.g. the user clicked a
    // routerLink), the browser URL has not been updated yet.  Dispatching a
    // query-param cleanup here would create a new navigation to the **old** URL
    // (minus the param), which cancels the intended navigation.
    if (this._router?.ngRouter.getCurrentNavigation()) {
      return
    }

    const persistenceKey =
      this.enableQueryParamPersistenceUnderKeySg()?.trim() ?? undefined
    if (!persistenceKey || !this._router) {
      return
    }

    this._router.actions.navigate.from('AtnDsAbstractTabs.OnDestroy').dispatch({
      queryParams: {
        plain: {
          [persistenceKey]: null,
        },
      },
      replaceUrl: true,
    })
  }
}
